import { StatusBadge } from "@/components/ui/status-badge";
import type { IntensitySignalBasis, OffPodSignalBasis } from "@/hooks/use-signals";

interface Props {
  basis: IntensitySignalBasis;
}

/**
 * Fit Score's "never a black box" rule applies to Suggested Intensity too —
 * this renders the raw inputs behind a suggestion so an RM never has to trust
 * a bare number. Shows the source months (Guidance or trailing-actuals
 * projection), the divisor, and the resulting average.
 */
export function SuggestionBasis({ basis }: Props) {
  return (
    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-1.5">
        {basis.projectedFromHistory ? (
          <StatusBadge status="pending">Projected from history — no Guidance on file</StatusBadge>
        ) : (
          <StatusBadge status="info">From Guidance</StatusBadge>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px]">
        <span>months: {basis.months.map((m) => m ?? "—").join(", ")}</span>
        <span>divisor: {basis.divisor}</span>
        <span>avg: {basis.avg.toFixed(1)}</span>
      </div>
    </div>
  );
}

const OFF_POD_SOURCE_LABEL: Record<OffPodSignalBasis["source"], string> = {
  guidance: "Forecast (Guidance)",
  actuals: "Logged (Actuals)",
  both: "Forecast + Logged",
};

/**
 * The "never a black box" basis for a resource_off_pod signal: why the Resource
 * was surfaced (forward Guidance, logged Actuals, or both) and the summed hours
 * behind each source. Explains to the RM what to weigh before adding them.
 */
export function OffPodBasis({ basis }: { basis: OffPodSignalBasis }) {
  return (
    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge status="info">{OFF_POD_SOURCE_LABEL[basis.source]}</StatusBadge>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px]">
        {basis.guidanceHours !== null && <span>forecast: {basis.guidanceHours}h</span>}
        {basis.actualsHours !== null && <span>logged: {basis.actualsHours}h</span>}
        <span>not on this Pod</span>
      </div>
    </div>
  );
}
